/**
 * Image  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { Type, defineElementaryComponent, createSubScope } from "@adapptio/lib-blueprint";

import {
	TViewComponentDefinition,
	TComponentReact,
	Image,
	ImageProps,
	isEventEnabled
} from "@adapptio/lib-components";
import { BaseEvents } from "../../base/BaseEvents";
import { termsEditor } from "../../terms";

interface ComponentImage_State {}

const ComponentImage_Props = {
	...ImageProps
};

const ComponentImage_Events = {
	...BaseEvents
};

const ComponentImage_Definition = defineElementaryComponent<
	typeof ComponentImage_Props,
	ComponentImage_State,
	typeof ComponentImage_Events
>({
	...termsEditor.components.image.component,

	name: "image",

	category: "content",

	icon: "mdi/image",

	events: ComponentImage_Events,

	docUrl: "...",

	order: 60,

	props: ComponentImage_Props,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: (spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentImage_React: TComponentReact<typeof ComponentImage_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {
	const _clickHandler = React.useMemo(() => {
		if (isEventEnabled(componentInstance.eventEnabled.click, componentInstance.componentMode)) {
			return () => componentInstance.eventTriggers.click((parentScope) => createSubScope(parentScope));
		}

		return;
	}, [
		componentInstance.componentMode,
		componentInstance.eventEnabled.click,
		componentInstance.eventTriggers.click
	]);

	return (
		<Image
			{...props}
			componentPath={componentInstance.safePath}
			componentMode={componentInstance.componentMode}
			classList={reactComponentClassList}
			forceRender={true}
			onClick={_clickHandler}
		/>
	);
};

export const ComponentImage: TViewComponentDefinition<typeof ComponentImage_Definition> = {
	...ComponentImage_Definition,
	reactComponent: ComponentImage_React
};
